import React from 'react';
import { enhanceLollipopHtml } from './lollipopEnhancer';

// ---------- utils ----------
const findRx = (files, rx) => files.find(f => rx.test(f?.name || ''));
const findAllRx = (files, rx) => files.filter(f => rx.test(f?.name || ''));

const base64DataURI = (b64, mime = 'application/octet-stream') =>
  `data:${mime};base64,${(b64 || '').trim()}`;

const getMime = (name = '') => {
  const n = name.toLowerCase();
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  return 'application/octet-stream';
};

// ---------- special renderers ----------
const LollipopRenderer = ({ section }) => {
  if (!section.htmlFile) return null;

  const htmlContent = section.htmlFile.data || '';
  const rawHtml = htmlContent.startsWith('data:')
    ? atob(htmlContent.split(',')[1])
    : htmlContent;
  const srcDoc = enhanceLollipopHtml(rawHtml);

  return (
    <div className="WSNG-lollipop-panel">
      <iframe
        srcDoc={srcDoc}
        title="Lollipop Visualization"
        style={{
          width: '100%',
          height: '700px',
          border: 'none',
          borderRadius: '8px',
          backgroundColor: '#fff'
        }}
      />
    </div>
  );
};

const GlobalAlleleFrequencyRenderer = ({ section }) => (
  <div className="WSNG-Global Allele Frequency-panel">
    <div className="WSNG-images-grid">
      {section.images.map((img, i) => (
        <div key={i} className="WSNG-image-card">
          <img src={base64DataURI(img.data, getMime(img.name))} alt={img.name} />
          <span className="WSNG-image-title">{img.name.split('/').pop()}</span>
        </div>
      ))}
    </div>
  </div>
);

// ---------- organizer (single-variant) ----------
export const organizeSingleVariant = (files, jobData) => {
  const findData = (rx) => findRx(files, rx)?.data || '';

  // --- 1. Variant Annotation ---
  // Match variant lookup files like chr13_32338103_G_GA.txt
  // Exclude specific analysis files (gwas, eqtl, pqtl, pgx, etc.)
  const mainVariantFile = findData(/^(?!.*\.(gwas|eqtl_catalog|eqtl_gtex|pqtl_otg|pqtl-epigraphdb|pgx)).*chr\d+_\d+_[ATCG]+_[ATCG]+\.txt$/i)
    || findData(/^(?!.*\.(gwas|eqtl_catalog|eqtl_gtex|pqtl_otg|pqtl-epigraphdb|pgx))[^/]*\.txt$/i);

  const annotationTables = mainVariantFile ? [
    { name: 'Variant Annotation', data: mainVariantFile, active: true }
  ] : [];

  const variantAnnotation = annotationTables.length ? { tables: annotationTables } : undefined;

  // --- 2. Lollipop Plot ---
  const lollipopHtml = findRx(files, /Lollipop\.html$/i);
  const lollipop = lollipopHtml ? {
    htmlFile: lollipopHtml,
    tables: []
  } : undefined;

  // --- 3. GWAS & QTLs ---
  const qtlTables = [
    { name: 'GWAS', data: findData(/\.gwas\.txt$/i) },
    { name: 'eQTL Catalog', data: findData(/\.eqtl_catalog\.txt$/i) },
    { name: 'eQTL GTEx', data: findData(/\.eqtl_gtex\.txt$/i) },
    { name: 'pQTL Open Targets', data: findData(/\.pqtl_otg\.txt$/i) },
    { name: 'pQTL EpigraphDB', data: findData(/\.pqtl-epigraphdb\.txt$/i) },
    { name: 'Pharmacogenomics (PGx)', data: findData(/\.pgx\.txt$/i) },
  ]
  .filter(t => t.data)
  .map((t, i) => ({ ...t, active: i === 0 }));

  const qtls = qtlTables.length ? { tables: qtlTables } : undefined;

  // --- 4. Global Allele Frequency ---
  const images = findAllRx(files, /AFMap\.png$/i);
  const GlobalAlleleFrequency = images.length ? { images } : undefined;

  // --- Assemble All Tabs ---
  const organized = {
    ...(variantAnnotation ? { 'Variant Annotation': variantAnnotation } : {}),
    ...(lollipop ? { 'Lollipop Plot': lollipop } : {}),
    ...(qtls ? { 'GWAS & QTLs': qtls } : {}),
    ...(GlobalAlleleFrequency ? { 'Global Allele Frequency': GlobalAlleleFrequency } : {}),
  };

  const renderers = {
    'Lollipop Plot': (props) => <LollipopRenderer {...props} />,
    'Global Allele Frequency': (props) => <GlobalAlleleFrequencyRenderer {...props} />,
  };

  const firstTab = variantAnnotation ? 'Variant Annotation' : (lollipop ? 'Lollipop Plot' : (qtls ? 'GWAS & QTLs' : 'Global Allele Frequency'));
  const initial = {
    tab: firstTab,
    sub: (annotationTables[0] || qtlTables[0] || {}).name
  };

  return { organized, renderers, initial };
};
